import React, { Fragment, useMemo } from 'react'
import {
  AttachmentLink,
  Heading,
  KennoFile,
  Paragraph,
} from '@pandiafi/design-system'
import { Trans, useTranslation } from 'react-i18next'
import { DateTime } from 'luxon'
import { ContextNotification } from '@pandiafi/components'

import {
  Contract,
  ContractTenant,
  ContractTerminationRequest,
} from '../../../../common/validations'
import { useTerminationReasonToLabel } from '../hooks/useTerminationReasonToLabel'
import { useTerminationDestinationToLabel } from '../hooks/useTerminationDestinationToLabel'
import { useShowingSuggestions } from '../hooks/useShowingSuggestions'
import { getTenantName } from '../../../utils/getTenantName'
import { ContractType } from '../../../../common/types'
import { resolveContractTerminationTerminology } from '../../ContractChanges/utils/resolveContractTerminationTerminology'
import { ibanCountryName } from '../utils/bankCountry'

import { Section, TextTable } from 'components'
import { valueOrEmpty } from 'utils'
import { useModuleConfig } from 'hooks/useModuleConfig/useModuleConfig'

interface PreviewData extends Omit<
  ContractTerminationRequest,
  | 'beneficiaries'
  | 'files'
  | 'id'
  | 'movingDestination'
  | 'status'
  | 'terminationReason'
> {
  files: KennoFile[] | string[]
  movingDestination: ContractTerminationRequest['movingDestination'] | ''
  terminationReason: ContractTerminationRequest['terminationReason'] | ''
  beneficiaries: Array<
    Omit<
      ContractTerminationRequest['beneficiaries'][number],
      'returnPercentage' | 'rooReturnPercentage'
    > & {
      existingBeneficiaryId?: number | null
      contractToTenantAssociationId?: number | null
      returnPercentage: number | null
      rooReturnPercentage: number | null
    }
  >
}

interface ContractTerminationRequestPreviewProps {
  contractTerminationRequest: PreviewData
  tenants: ContractTenant[]
  contract: Contract
  confirmButtonLabel: string
  backToEditButtonLabel?: string
}

export const ContractTerminationRequestPreview: React.FC<
  ContractTerminationRequestPreviewProps
> = ({
  contractTerminationRequest,
  tenants,
  contract,
  confirmButtonLabel,
  backToEditButtonLabel,
}) => {
  const { t, i18n } = useTranslation(['common', 'terminateContract'])
  const { terminationReasonToLabel } = useTerminationReasonToLabel()
  const { terminationDestinationToLabel } = useTerminationDestinationToLabel()
  const { showingSuggestions } = useShowingSuggestions()
  const moduleConfig = useModuleConfig()
  const showShowingSection =
    moduleConfig.terminateContract?.showingVisible ?? true

  const {
    movingOutDate,
    endDate,
    displaysSelf,
    presenterTenantUuid,
    additionalDetails,
    givingContactInfoAllowed,
    masterKeyAccessAllowed,
    hasPetsInApartment,
    terminationReason,
    movingDestination,
    newAddress,
    beneficiaries,
    files,
    collateralLender,
    isCollateral,
  } = contractTerminationRequest
  const {
    recipientName,
    streetAddress,
    streetAddressLine2,
    postalCode,
    city,
    countrySubdivision,
    countryName,
    countryCode,
  } = newAddress || {}

  const filesList = useMemo(() => {
    if (!files.length) {
      return valueOrEmpty()
    }

    return files.map((file) => {
      if (typeof file === 'string') {
        return (
          <Paragraph className="mb-1.5 last:mb-0 break-all">{file}</Paragraph>
        )
      }

      return (
        <AttachmentLink
          key={file.fileName}
          href={file._objectUrl}
          text={file.metadata?.originalFileName ?? file.fileName}
          icon={file.fileType === 'image' ? 'image' : 'file'}
          target="_blank"
          className="mb-1.5 last:mb-0 break-all"
        />
      )
    })
  }, [files])

  const isAddressProvided =
    newAddress &&
    Object.values(newAddress).some((value) => value && value.trim() !== '')

  const selectedPresenter = presenterTenantUuid
    ? tenants.find((tenant) => tenant.tenantUuid === presenterTenantUuid)
    : null

  const isCurrentTenantSelectedPresenter =
    selectedPresenter?.id === contract.tenant.id
  const terminology = resolveContractTerminationTerminology(
    contract.contractType
  )

  return (
    <>
      <div className="bg-brand-1 py-8 rounded-t-lg -mt-7 -mx-7 mb-10 text-center">
        <Heading size="l" element="h1">
          {t(terminology.previewHeading)}
        </Heading>
      </div>
      <Section className="mb-12">
        <ContextNotification type="info" textAlign="center">
          <div className="space-y-2">
            <Paragraph>
              {t('terminateContract:texts.confirmationInstructionReview')}
            </Paragraph>
            <Paragraph>
              <Trans
                t={t}
                i18nKey="terminateContract:texts.confirmationInstructionConfirm"
                values={{
                  confirmButtonLabel,
                }}
                components={{ strong: <strong /> }}
              />
            </Paragraph>
            <Paragraph>{t(terminology.sentAfterConfirmation)}</Paragraph>
            <Paragraph>
              <strong>{t(terminology.returnFromAuthReminder)}</strong>
            </Paragraph>
            {backToEditButtonLabel && (
              <Paragraph>
                <Trans
                  t={t}
                  i18nKey="terminateContract:texts.confirmationInstructionEdit"
                  values={{ backToEditButtonLabel }}
                  components={{ strong: <strong /> }}
                />
              </Paragraph>
            )}
          </div>
        </ContextNotification>
      </Section>
      <Section className="mb-12">
        <Heading size="l" element="h2" className="mb-4">
          {t('terminateContract:headings.basicInfo')}
        </Heading>
        <TextTable className="min-tablet:text-lg">
          {tenants.map((tenant, index) => (
            <TextTable.Row key={tenant.id}>
              <TextTable.Cell>
                {index === 0 ? t(terminology.submittedLabel) : ''}
              </TextTable.Cell>
              <TextTable.Cell>{getTenantName(tenant)}</TextTable.Cell>
            </TextTable.Row>
          ))}
          <TextTable.Row>
            <TextTable.Cell>
              {t('terminateContract:labels.movingOutDate')}
            </TextTable.Cell>
            <TextTable.Cell>
              {movingOutDate ?? t('common:notKnown')}
            </TextTable.Cell>
          </TextTable.Row>
          <TextTable.Row>
            <TextTable.Cell>
              {t('terminateContract:labels.contractEndDate')}
            </TextTable.Cell>
            <TextTable.Cell>
              {endDate
                ? DateTime.fromISO(endDate).toFormat('dd.MM.yyyy')
                : t('common:notKnown')}
            </TextTable.Cell>
          </TextTable.Row>
          <TextTable.Row>
            <TextTable.Cell>{t(terminology.reasonLabel)}</TextTable.Cell>
            <TextTable.Cell>
              {terminationReasonToLabel(terminationReason)}
            </TextTable.Cell>
          </TextTable.Row>
          <TextTable.Row>
            <TextTable.Cell>
              {t('terminateContract:labels.movingDestination')}
            </TextTable.Cell>
            <TextTable.Cell>
              {movingDestination
                ? terminationDestinationToLabel(movingDestination)
                : t('common:notKnown')}
            </TextTable.Cell>
          </TextTable.Row>
          <TextTable.Row>
            <TextTable.Cell>
              {t('terminateContract:labels.files')}
            </TextTable.Cell>
            <TextTable.Cell>{filesList}</TextTable.Cell>
          </TextTable.Row>
        </TextTable>
      </Section>
      <Section className="mb-12">
        <Heading size="l" element="h2" className="mb-8">
          {t('terminateContract:headings.paymentAndContactInfo')}
        </Heading>
        {contract.contractType === ContractType.RIGHT_OF_OCCUPANCY && (
          <TextTable className="min-tablet:text-lg mb-6">
            <TextTable.Row>
              <TextTable.Cell>
                {t(
                  'terminateContract:labels.rightOfOccupancyCollateralLocation'
                )}
              </TextTable.Cell>
              <TextTable.Cell>
                {isCollateral
                  ? `${t(
                      'terminateContract:rightOfOccupancyCollateralLocationSuggestions.bank'
                    )}: ${collateralLender || '-'}`
                  : t(
                      'terminateContract:rightOfOccupancyCollateralLocationSuggestions.holder'
                    )}
              </TextTable.Cell>
            </TextTable.Row>
          </TextTable>
        )}
        {beneficiaries.map(
          (
            {
              beneficiaryName,
              removeExistingBeneficiary,
              iban,
              bic,
              returnPercentage,
              rooReturnPercentage,
              email,
              phoneNumber,
            },
            index
          ) => {
            const bankCountryName = ibanCountryName(iban, i18n.language)

            return (
              !removeExistingBeneficiary && (
                // eslint-disable-next-line @eslint-react/no-array-index-key
                <Fragment key={`${beneficiaryName}-${index}`}>
                  <Heading size="m" element="h3" className="my-4">
                    {beneficiaryName}
                  </Heading>
                  <TextTable className="min-tablet:text-lg">
                    <TextTable.Row>
                      <TextTable.Cell>
                        {t('terminateContract:labels.beneficiaryName')}
                      </TextTable.Cell>
                      <TextTable.Cell>{beneficiaryName ?? '-'}</TextTable.Cell>
                    </TextTable.Row>
                    {contract.contractType ===
                      ContractType.RIGHT_OF_OCCUPANCY && (
                      <TextTable.Row>
                        <TextTable.Cell>
                          {t('terminateContract:labels.rooReturnPercentage')}
                        </TextTable.Cell>
                        <TextTable.Cell>
                          {rooReturnPercentage === null
                            ? '-'
                            : `${rooReturnPercentage}%`}
                        </TextTable.Cell>
                      </TextTable.Row>
                    )}
                    <TextTable.Row>
                      <TextTable.Cell>
                        {t('terminateContract:labels.iban')}
                      </TextTable.Cell>
                      <TextTable.Cell>{iban ?? '-'}</TextTable.Cell>
                    </TextTable.Row>
                    {bankCountryName && (
                      <TextTable.Row>
                        <TextTable.Cell>
                          {t('terminateContract:labels.bankCountry')}
                        </TextTable.Cell>
                        <TextTable.Cell>{bankCountryName}</TextTable.Cell>
                      </TextTable.Row>
                    )}
                    {bic && (
                      <TextTable.Row>
                        <TextTable.Cell>
                          {t('terminateContract:labels.bic')}
                        </TextTable.Cell>
                        <TextTable.Cell>{bic ?? '-'}</TextTable.Cell>
                      </TextTable.Row>
                    )}
                    <TextTable.Row>
                      <TextTable.Cell>
                        {t('terminateContract:labels.returnPercentage')}
                      </TextTable.Cell>
                      <TextTable.Cell>
                        {returnPercentage || '- '}%
                      </TextTable.Cell>
                    </TextTable.Row>
                    <TextTable.Row>
                      <TextTable.Cell>
                        {t('terminateContract:labels.email')}
                      </TextTable.Cell>
                      <TextTable.Cell>{valueOrEmpty(email)}</TextTable.Cell>
                    </TextTable.Row>
                    <TextTable.Row>
                      <TextTable.Cell>
                        {t('terminateContract:labels.phoneNumber')}
                      </TextTable.Cell>
                      <TextTable.Cell>
                        {valueOrEmpty(phoneNumber)}
                      </TextTable.Cell>
                    </TextTable.Row>
                  </TextTable>
                </Fragment>
              )
            )
          }
        )}
      </Section>
      {isAddressProvided && (
        <Section className="mb-12">
          <Heading size="l" element="h2" className="mb-4">
            {t('terminateContract:headings.newAddress')}
          </Heading>
          <TextTable className="min-tablet:text-lg">
            <TextTable.Row>
              <TextTable.Cell>
                {t('terminateContract:labels.recipientName')}
              </TextTable.Cell>
              <TextTable.Cell>{recipientName}</TextTable.Cell>
            </TextTable.Row>
            <TextTable.Row>
              <TextTable.Cell>
                {t('terminateContract:labels.streetAddress')}
              </TextTable.Cell>
              <TextTable.Cell>{streetAddress}</TextTable.Cell>
            </TextTable.Row>
            {streetAddressLine2 && (
              <TextTable.Row>
                <TextTable.Cell>
                  {t('terminateContract:labels.streetAddressLine2')}
                </TextTable.Cell>
                <TextTable.Cell>{streetAddressLine2}</TextTable.Cell>
              </TextTable.Row>
            )}
            <TextTable.Row>
              <TextTable.Cell>
                {t('terminateContract:labels.postalCode')}
              </TextTable.Cell>
              <TextTable.Cell>{postalCode}</TextTable.Cell>
            </TextTable.Row>
            <TextTable.Row>
              <TextTable.Cell>
                {t('terminateContract:labels.city')}
              </TextTable.Cell>
              <TextTable.Cell>{city}</TextTable.Cell>
            </TextTable.Row>
            {countrySubdivision && (
              <TextTable.Row>
                <TextTable.Cell>
                  {t('terminateContract:labels.countrySubdivision')}
                </TextTable.Cell>
                <TextTable.Cell>{countrySubdivision}</TextTable.Cell>
              </TextTable.Row>
            )}
            {countryCode && countryCode !== 'FI' && (
              <TextTable.Row>
                <TextTable.Cell>
                  {t('terminateContract:labels.countryName')}
                </TextTable.Cell>
                <TextTable.Cell>{countryName}</TextTable.Cell>
              </TextTable.Row>
            )}
          </TextTable>
        </Section>
      )}

      {showShowingSection && (
        <Section className="mb-12">
          <Heading size="l" element="h2" className="mb-4">
            {t('terminateContract:headings.showing')}
          </Heading>
          {displaysSelf && isCurrentTenantSelectedPresenter && (
            <ContextNotification type="info" className="mb-4">
              {t('terminateContract:texts.selectedAsPresenter')}
            </ContextNotification>
          )}
          <TextTable className="min-tablet:text-lg">
            <TextTable.Row>
              <TextTable.Cell>
                {t('terminateContract:labels.showing')}
              </TextTable.Cell>
              <TextTable.Cell>
                {displaysSelf
                  ? showingSuggestions[0].label
                  : showingSuggestions[1].label}
              </TextTable.Cell>
            </TextTable.Row>
            <TextTable.Row>
              <TextTable.Cell>
                {t('terminateContract:labels.presenter')}
              </TextTable.Cell>
              <TextTable.Cell>
                {selectedPresenter
                  ? getTenantName(selectedPresenter)
                  : t('common:notKnown')}
              </TextTable.Cell>
            </TextTable.Row>
            <TextTable.Row>
              <TextTable.Cell>
                {t('terminateContract:labels.additionalDetails')}
              </TextTable.Cell>
              <TextTable.Cell>{valueOrEmpty(additionalDetails)}</TextTable.Cell>
            </TextTable.Row>
            <TextTable.Row>
              <TextTable.Cell>
                {t('terminateContract:labels.givingContactInfoAllowed')}
              </TextTable.Cell>
              <TextTable.Cell>
                {givingContactInfoAllowed ? t('common:yes') : t('common:no')}
              </TextTable.Cell>
            </TextTable.Row>
            <TextTable.Row>
              <TextTable.Cell>
                {t('terminateContract:labels.masterKeyAccessAllowed')}
              </TextTable.Cell>
              <TextTable.Cell>
                {masterKeyAccessAllowed ? t('common:yes') : t('common:no')}
              </TextTable.Cell>
            </TextTable.Row>
            <TextTable.Row>
              <TextTable.Cell>
                {t('terminateContract:labels.hasPetsInApartment')}
              </TextTable.Cell>
              <TextTable.Cell>
                {hasPetsInApartment ? t('common:yes') : t('common:no')}
              </TextTable.Cell>
            </TextTable.Row>
          </TextTable>
        </Section>
      )}

      {!showShowingSection && givingContactInfoAllowed && (
        <Section className="mb-12">
          <Heading size="l" element="h2" className="mb-4">
            {t('terminateContract:headings.contactInformation')}
          </Heading>
          <TextTable className="min-tablet:text-lg">
            <TextTable.Row>
              <TextTable.Cell>
                {t('terminateContract:labels.givingContactInfoAllowed')}
              </TextTable.Cell>
              <TextTable.Cell>
                {givingContactInfoAllowed ? t('common:yes') : t('common:no')}
              </TextTable.Cell>
            </TextTable.Row>
          </TextTable>
        </Section>
      )}
    </>
  )
}
